import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { GlobeIcon, MapPinIcon, ClockIcon, CheckIcon, WhatsAppIcon, PhoneIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [relatedPackages, setRelatedPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackage();
  }, [id]);

  const fetchPackage = async () => {
    try {
      setLoading(true);
      const data = await api.getPackage(id);
      setPkg(data);
      setError(null);
      
      // Fetch related packages (same category)
      if (data.category) {
        const allPackages = await api.getPackages('all');
        const related = allPackages
          .filter(p => p.category === data.category && p.id !== data.id)
          .slice(0, 3);
        setRelatedPackages(related);
      }
    } catch (err) {
      console.error('Error fetching package:', err);
      setError('Failed to load package. Please try again later.');
      setPkg(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-blue"></div>
          <p className="mt-4 text-gray-600">Loading package details...</p>
        </div>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Package Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The package you\'re looking for doesn\'t exist.'}</p>
          <Link to="/packages" className="text-primary-blue hover:text-primary-orange font-semibold">
            ← Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      <SEO
        title={pkg.title}
        description={pkg.description ? (pkg.description.substring(0, 160) + (pkg.description.length > 160 ? '...' : '')) : `${pkg.title} - ${pkg.destination} tour package, ${pkg.duration}. Book with Happy Yatra Travels.`}
        image={pkg.image_url}
        canonical={`/packages/${id}`}
        keywords={`${pkg.title}, ${pkg.destination}, tour package, ${pkg.duration}, Happy Yatra Travels`}
      />
      {/* Hero Image Section */}
      <section className="relative h-96 md:h-[500px] overflow-hidden">
        {pkg.image_url ? (
          <img
            src={pkg.image_url}
            alt={pkg.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-light-blue to-light-orange flex items-center justify-center">
            <GlobeIcon className="w-32 h-32 text-primary-blue" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-2 bg-primary-orange text-white text-sm font-semibold rounded-full mb-4 capitalize">
                {pkg.category}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
                {pkg.title}
              </h1>
              <div className="flex items-center gap-6 text-white/90 flex-wrap">
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-5 h-5" />
                  <span className="font-medium">{pkg.destination}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-5 h-5" />
                  <span>{pkg.duration}</span>
                </div>
                <div className="text-2xl font-bold text-primary-orange">
                  {pkg.price}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back Button */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-blue transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Packages
          </button>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Package Overview</h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-8">
              {pkg.description}
            </p>

            {/* Highlights Section */}
            {pkg.highlights && pkg.highlights.length > 0 && (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-primary-blue mb-6">Package Highlights</h3>
                <ul className="space-y-4">
                  {pkg.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <CheckIcon className="w-6 h-6 text-primary-orange" />
                      </div>
                      <span className="text-gray-700 text-lg">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Package Details */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <MapPinIcon className="w-6 h-6 text-primary-blue" />
                  <h4 className="text-xl font-semibold text-gray-900">Destination</h4>
                </div>
                <p className="text-gray-600">{pkg.destination}</p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <ClockIcon className="w-6 h-6 text-primary-blue" />
                  <h4 className="text-xl font-semibold text-gray-900">Duration</h4>
                </div>
                <p className="text-gray-600">{pkg.duration}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-blue to-dark-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Book This Package?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get in touch with our travel experts to customize this package for you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/917718838808?text=Hi, I'm interested in ${pkg.title} package to ${pkg.destination}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-primary-orange text-white rounded-xl font-semibold hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg inline-flex items-center justify-center gap-2"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </a>
            <a
              href="tel:+917718838808"
              className="px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-xl font-semibold hover:bg-white/30 border-2 border-white/50 transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <PhoneIcon className="w-5 h-5" />
              Call Us Now
            </a>
          </div>
          <div className="mt-8">
            <p className="text-3xl font-bold text-primary-orange">{pkg.price}</p>
            <p className="text-white/80 text-sm mt-2">Starting Price</p>
          </div>
        </div>
      </section>

      {/* Related Packages */}
      {relatedPackages.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Packages</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPackages.map((relatedPkg) => (
                <Link
                  key={relatedPkg.id}
                  to={`/packages/${relatedPkg.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  {relatedPkg.image_url ? (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedPkg.image_url}
                        alt={relatedPkg.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-light-blue to-light-orange flex items-center justify-center">
                      <GlobeIcon className="w-16 h-16 text-primary-blue" />
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-xs font-semibold text-primary-orange capitalize">{relatedPkg.category}</span>
                    <h3 className="text-lg font-bold text-gray-900 mt-2 mb-2 line-clamp-2">
                      {relatedPkg.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{relatedPkg.description}</p>
                    <p className="text-xl font-bold text-primary-orange">{relatedPkg.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PackageDetail;
